import { useSearchParams } from "react-router-dom";
import type {
  MetricInvestigation,
  MetricInvestigationLimitation,
  MetricInvestigationSide,
  MetricInvestigationUnknownReason,
  MetricInvestigationVerdict,
  Section,
} from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { Panel } from "@/components/primitives";
import { num, pct } from "@/lib/format";

/**
 * 저장 지표 기준선 대비 이동 조사 카드 — ADR 0055 · Issue #159
 * --------------------------------------------------------------------------
 * **조회 전용**입니다. 값은 `useWorkloadDetail`이 이미 받아 온 같은 응답의
 * `metricInvestigation` 섹션에서 옵니다 — 새 요청 훅도 폴링도 없습니다. 토글은 URL의
 * `?investigation=errorRatioShift`만 바꾸고, 다른 파라미터는 그대로 둡니다.
 *
 * - **가설 검정이 아닙니다.** p-value·신뢰도·확률을 보이지 않고 원인을 말하지 않습니다.
 *   `no_shift_detected`에는 "문제가 없다는 뜻이 아님"을 언제나 붙입니다.
 * - 판정 줄은 **중립 텍스트 + 방향 기호**입니다. 상태 색(good/warning/serious/critical)과
 *   상태 배지를 쓰지 않습니다 — 이동은 좋고 나쁨의 판정이 아닙니다.
 * - 와이어는 **비율 원값**이고 `%`·`%p` 변환은 이 포맷터만 합니다(0.1 → 10.00%p).
 * - 출처 없는 미상은 장애로 칠하지 않습니다(`data-outage="false"`). 한계는 응답의
 *   `limitations` 배열만 그립니다 — 측정값 없는 화면에 외삽 한계를 보이지 않습니다.
 */

/**
 * 미상 사유 → 문구 id. union 전부를 덮어야 타입 검사를 통과하고, id를 조립하지 않습니다.
 * 같은 운영자 행동이면 같은 문장을 쓰고, 이 화면에서도 참인 기존 문구만 재사용합니다.
 */
const REASON_MESSAGE: Record<MetricInvestigationUnknownReason, string> = {
  investigation_not_configured: "common.rolloutCompare.reason.notConfigured",
  investigation_not_requested: "common.metricInvestigation.reason.notRequested",
  slo_not_configured: "common.slo.reason.notConfigured",
  kind_unsupported: "common.metricInvestigation.reason.kindUnsupported",
  workload_uid_missing: "common.slo.reason.scopeUnsupported",
  scope_unsupported: "common.slo.reason.scopeUnsupported",
  source_not_configured: "common.slo.reason.sourceUnverified",
  source_provenance_unverified: "common.slo.reason.sourceUnverified",
  source_ingestion_unqualified: "common.rolloutCompare.reason.ingestionUnqualified",
  query_unsupported: "common.slo.reason.queryUnsupported",
  baseline_before_trusted_bound: "common.metricInvestigation.reason.beforeBound",
  baseline_no_data: "common.rolloutCompare.reason.noData",
  baseline_history_short: "common.metricInvestigation.reason.historyShort",
  baseline_insufficient_coverage: "common.rolloutCompare.reason.coverage",
  baseline_low_traffic: "common.rolloutCompare.reason.lowTraffic",
  current_no_data: "common.rolloutCompare.reason.noData",
  current_insufficient_coverage: "common.rolloutCompare.reason.coverage",
  current_low_traffic: "common.rolloutCompare.reason.lowTraffic",
  current_stale_data: "common.rolloutCompare.reason.stale",
  invalid_status_share: "common.rolloutCompare.reason.invalidStatusShare",
  invalid_sample: "common.slo.reason.invalidResponse",
  invalid_ratio: "common.slo.reason.invalidResponse",
  cardinality_exceeded: "common.metricInvestigation.reason.tooLarge",
  result_too_large: "common.metricInvestigation.reason.tooLarge",
  invalid_response: "common.slo.reason.invalidResponse",
  scope_mismatch: "common.slo.reason.invalidResponse",
  upstream_unavailable: "common.slo.reason.upstream",
  timeout: "common.slo.reason.upstream",
  circuit_open: "common.slo.reason.upstream",
};

/** 실제 상류 장애만 장애로 그립니다. 나머지는 구성·관측·계약의 사실입니다. */
const OUTAGE_REASONS: ReadonlySet<string> = new Set(["upstream_unavailable", "timeout", "circuit_open"]);

/** 한계 코드 → 문구 id. 계약과 UI가 같은 한계를 말합니다. */
const LIMITATION_MESSAGE: Record<MetricInvestigationLimitation, string> = {
  increase_extrapolated: "common.rolloutCompare.limit.extrapolated",
  bucket_median_unweighted: "common.metricInvestigation.limit.unweighted",
  no_significance_test: "common.metricInvestigation.limit.noSignificance",
  seasonality_not_modeled: "common.metricInvestigation.limit.seasonality",
  unobserved_series_excluded: "common.rolloutCompare.limit.unobserved",
  counter_resets_absorbed_by_increase: "common.rolloutCompare.limit.resets",
  qualification_is_attestation: "common.metricInvestigation.limit.attestation",
};

/** 판정 → 방향 기호와 문구 id. 색이 아니라 기호와 텍스트가 방향을 말합니다. */
const VERDICT: Record<MetricInvestigationVerdict, readonly [string, string]> = {
  shift_up: ["↑", "common.metricInvestigation.verdict.up"],
  shift_down: ["↓", "common.metricInvestigation.verdict.down"],
  no_shift_detected: ["–", "common.metricInvestigation.verdict.none"],
};

const BASELINE = "common.metricInvestigation.window.baseline";
const CURRENT = "common.metricInvestigation.window.current";
const SELECTOR = "errorRatioShift";

/** 비율 원값 → %. 계약에 퍼센트를 싣지 않으므로 변환은 여기서만 합니다. */
const ratio = (v: number) => pct(v * 100, 2);
/** 부호를 명시합니다. */
const signed = (text: string, v: number) => (v > 0 ? `+${text}` : text);
/** 비율 원값의 차이 → %p. */
const points = (v: number) => signed(`${ratio(v)}p`, v);
const clock = (iso: string) => iso.replace("T", " ").replace("Z", "");

export function MetricInvestigationCard({
  section,
  referenceIso,
}: {
  section?: Section<MetricInvestigation>;
  referenceIso?: string;
}) {
  const { t } = useT("common");
  const [params, setParams] = useSearchParams();
  const requested = params.get("investigation") === SELECTOR;
  const data = section?.data;
  const reason = section?.status === "degraded" ? (section.reason as MetricInvestigationUnknownReason) : undefined;
  const outage = reason !== undefined && OUTAGE_REASONS.has(reason);
  const base = data?.baseline;
  const cur = data?.current;
  const effect = data?.effect;
  const w = data?.windows;
  const missing = t("common.rolloutCompare.sideMissing");

  /** 요청 여부만 URL에 둡니다. 다른 선택(목표·앵커·범위)은 그대로 남깁니다. */
  const toggle = () => {
    const next = new URLSearchParams(params);
    if (requested) next.delete("investigation");
    else next.set("investigation", SELECTOR);
    setParams(next, { replace: true });
  };

  const median = (s?: MetricInvestigationSide) => (s?.median !== undefined ? ratio(s.median) : missing);
  const valid = (s?: MetricInvestigationSide) => (s ? `${num(s.validBuckets)}/${num(s.totalBuckets)}` : missing);
  const population = (s: MetricInvestigationSide) =>
    t("common.rolloutCompare.population", {
      series: num(s.observedSeries),
      pods: num(s.observedPods),
      eligible: num(s.eligibleEvents),
    });
  const windowOf = reason?.startsWith("baseline_") ? BASELINE : reason?.startsWith("current_") ? CURRENT : undefined;

  return (
    <Panel
      title={t("common.metricInvestigation.title")}
      section={section}
      referenceIso={referenceIso}
      className="metric-investigation-panel"
      outage={outage}
      actions={
        <button
          type="button"
          className="chip"
          aria-pressed={requested}
          data-testid="investigation-toggle"
          onClick={toggle}
        >
          {t("common.metricInvestigation.toggle")}
        </button>
      }
    >
      <div className="grid" data-testid="investigation-card">
        <p className="muted">{t("common.metricInvestigation.method")}</p>

        {effect && (
          <div className="row" data-testid="investigation-verdict" data-verdict={effect.verdict}>
            <span aria-hidden="true">{VERDICT[effect.verdict][0]}</span>
            <strong>{t(VERDICT[effect.verdict][1])}</strong>
            {effect.verdict === "no_shift_detected" && (
              <span className="muted" data-testid="investigation-none-note">
                {t("common.metricInvestigation.verdict.noneNote")}
              </span>
            )}
          </div>
        )}

        {base && cur && (
          <table className="table" data-testid="investigation-table">
            <caption className="visually-hidden">{t("common.metricInvestigation.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("common.rolloutCompare.metric.errorRatio")}</th>
                <th scope="col">{t(BASELINE)}</th>
                <th scope="col">{t(CURRENT)}</th>
                <th scope="col">{t("common.rolloutCompare.deltaHeader")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{t("common.metricInvestigation.row.median")}</th>
                <td className="num" data-testid="investigation-baseline-median">{median(base)}</td>
                <td className="num" data-testid="investigation-current-median">{median(cur)}</td>
                <td className="num" data-testid="investigation-delta">{effect ? points(effect.absolute) : "—"}</td>
              </tr>
              <tr>
                <th scope="row">{t("common.metricInvestigation.row.mad")}</th>
                <td className="num" data-testid="investigation-baseline-mad">
                  {base.mad !== undefined ? ratio(base.mad) : missing}
                </td>
                <td className="num">—</td>
                <td className="num">—</td>
              </tr>
              <tr>
                <th scope="row">{t("common.metricInvestigation.row.valid")}</th>
                <td className="num" data-testid="investigation-baseline-valid">{valid(base)}</td>
                <td className="num" data-testid="investigation-current-valid">{valid(cur)}</td>
                <td className="num">—</td>
              </tr>
            </tbody>
          </table>
        )}

        <div className="facts">
          {effect && (
            <>
              <div className="fact">
                <span className="fact__label">{t("common.metricInvestigation.threshold", { value: `${ratio(effect.threshold)}p` })}</span>
                <span className="fact__value" data-testid="investigation-basis">
                  {t(
                    effect.thresholdBasis === "robust_scale"
                      ? "common.metricInvestigation.basis.robust"
                      : "common.metricInvestigation.basis.budget",
                  )}
                </span>
              </div>
              <div className="fact">
                <span className="fact__value" data-testid="investigation-distance">
                  {effect.robustDistance !== undefined
                    ? t("common.metricInvestigation.distance", { value: signed(effect.robustDistance.toFixed(2), effect.robustDistance) })
                    : t("common.metricInvestigation.distanceUndefined")}
                  {" · "}
                  {effect.relative !== undefined
                    ? t("common.rolloutCompare.relative", { value: signed(ratio(effect.relative), effect.relative) })
                    : t("common.metricInvestigation.relativeUndefined")}
                </span>
              </div>
              <div className="fact">
                <span className="fact__value" data-testid="investigation-exceedance">
                  {t("common.metricInvestigation.exceedance", {
                    currentAbove: num(effect.currentAbove),
                    currentBelow: num(effect.currentBelow),
                    currentTotal: num(cur?.validBuckets ?? 0),
                    baselineAbove: num(effect.baselineAbove),
                    baselineBelow: num(effect.baselineBelow),
                    baselineTotal: num(base?.validBuckets ?? 0),
                  })}
                </span>
              </div>
            </>
          )}
          {base && cur && (
            <>
              <div className="fact">
                <span className="fact__label">{t("common.slo.populationLabel")}</span>
                <span className="fact__value" data-testid="investigation-population">
                  {`${t(BASELINE)}: ${population(base)} · ${t(CURRENT)}: ${population(cur)}`}
                </span>
              </div>
              {data?.cohort && (
                <div className="fact">
                  <span className="fact__label">{t("common.rolloutCompare.cohortLabel")}</span>
                  <span className="fact__value" data-testid="investigation-cohort">
                    {t("common.metricInvestigation.cohort", {
                      both: num(data.cohort.seriesBoth),
                      baselineOnly: num(data.cohort.seriesBaselineOnly),
                      currentOnly: num(data.cohort.seriesCurrentOnly),
                    })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {w && (
          <p className="muted" data-testid="investigation-window">
            {t("common.metricInvestigation.windowDetail", {
              baselineStart: clock(w.baselineStart),
              baselineEnd: clock(w.baselineEnd),
              currentStart: clock(w.currentStart),
              currentEnd: clock(w.currentEnd),
            })}
          </p>
        )}

        {data && data.limitations.length > 0 && (
          <ul className="muted" data-testid="investigation-limitations">
            {data.limitations.map((l) => (
              <li key={l}>{t(LIMITATION_MESSAGE[l])}</li>
            ))}
          </ul>
        )}

        {reason && (
          <div
            className={`state${outage ? " state--degraded" : ""}`}
            data-testid="investigation-unknown"
            data-outage={outage ? "true" : "false"}
          >
            <span className="state__glyph" aria-hidden="true">
              {outage ? "!" : "·"}
            </span>
            <span className="state__title">{t("common.slo.unknownTitle")}</span>
            <span className="state__detail" data-testid="investigation-reason">
              {windowOf ? t(REASON_MESSAGE[reason], { window: t(windowOf) }) : t(REASON_MESSAGE[reason])}
            </span>
            {!outage && (
              <span className="muted" data-testid="investigation-not-outage">
                {t("common.rolloutCompare.notAnOutage")}
              </span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
